import { computed, InjectionKey, reactive, toRefs } from 'vue';
import { RepositoryFactory, Repositories } from '@/repositories/RepositoryFactory';

const CouponRepository = RepositoryFactory.get('coupon') as Repositories['coupon'];
import { CustomerCoupon } from '@/repositories/couponRepository';

type State = {
  customerCoupons: CustomerCoupon[];
};

/**
 * 一度にまとめて使用できるクーポン上限数
 * バックエンドで実行するトランザクションの都合上、10程度が上限
 * CS-6837: QRコードリーダーの読み取り性能を加味して 10 -> 6 に調整
 */
const MAX_COUPONS_SIZE_AT_ONE_TIME = 10;

/**
 * 複数の顧客クーポンを扱う
 */
export const useMultipleCoupon = () => {
  const state = reactive<State>({
    customerCoupons: [],
  });

  const length = computed(() => state.customerCoupons.length);
  const addable = computed(() => length.value < MAX_COUPONS_SIZE_AT_ONE_TIME);

  const add = (selectedCustomerCoupon: CustomerCoupon): void => {
    if (!addable.value) return;
    state.customerCoupons.push(selectedCustomerCoupon);
  };

  const remove = (id: CustomerCoupon['id']): void => {
    const index = state.customerCoupons.findIndex(coupon => coupon.id === id);
    state.customerCoupons.splice(index, 1);
  };

  const clear = () => {
    state.customerCoupons.splice(0);
  };

  const has = (id: CustomerCoupon['id']): boolean => {
    return !!state.customerCoupons.find(coupon => coupon.id === id);
  };

  /**
   * クーポンを複数枚同時に使用する
   * @param {string} [shopId]
   */
  const useMultipleCustomerCoupon = async (coupons: CustomerCoupon[], shopId?: string) => {
    return await CouponRepository.bulkUseCustomerCoupon(
      coupons.map(customerCoupon => customerCoupon.id),
      shopId
    );
  };

  return {
    ...toRefs(state),
    useMultipleCustomerCoupon,
    add,
    remove,
    clear,
    has,
    addable,
    length,
  };
};

type MultipleCouponType = ReturnType<typeof useMultipleCoupon>;
export const MultipleCouponKey: InjectionKey<MultipleCouponType> = Symbol('MultipleCoupon');
